// src/widgets/GroupMembersModal.jsx
import { useEffect, useMemo, useState, useCallback } from "react"
import { X, Trash2, LoaderCircle, Search, Users as UsersIcon, Briefcase, Users, Plus } from "lucide-react"
import Select from "react-select"
import { motion, AnimatePresence } from "framer-motion"
import ConfirmModal from "@/widgets/ConfirmModal"
import { getGroupMembers, removeGroupMember, addGroupMember, getLearners, getGroups, getJobTitles, getPrefixes } from "@/api/kuizy"
import DataTable from "@/widgets/DataTable"

const phoneDisp = u => ((u.phone_code || "") + (u.phone || "")) || "-"

export default function GroupMembersModal({ groupId, groupName, onClose }) {
  const [prefixes, setPrefixes] = useState([])
  const [groups, setGroups] = useState([])
  const [jobs, setJobs] = useState([])
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [memberInput, setMI] = useState("")
  const [memberSearch, setMS] = useState("")
  const [listGroupF, setListGroupF] = useState({ value: "", label: "ทั้งหมด" })
  const [listJobF, setListJobF] = useState({ value: "", label: "ทั้งหมด" })
  const [pickerOpen, setPickerOpen] = useState(false)
  const [alert, setAlert] = useState({ open: false, msg: "" })
  const [delItem, setDel] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [bulkConfirm, setBulkConfirm] = useState(false)
  const [bulkDeleting, setBulkDeleting] = useState(false)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(() => parseInt(localStorage.getItem("dt:groupMembers:perPage")) || 10)
  const [sortBy, setSortBy] = useState("joined_at")
  const [sortDir, setSortDir] = useState("desc")
  const [serverPaged, setServerPaged] = useState(false)
  const [selectedMap, setSelectedMap] = useState({})
  const [selectedRows, setSelectedRows] = useState({})

  useEffect(() => { localStorage.setItem("dt:groupMembers:perPage", String(perPage)) }, [perPage])

  const fullName = useCallback(u => {
    const pre = prefixes.find(p => p.id === u.prefix_id)?.name_th || prefixes.find(p => p.code === u.prefix)?.name_th || u.prefix || ""
    const fn = u.first_name || u.name || ""
    const ln = u.last_name || ""
    return `${pre ? pre + " " : ""}${fn} ${ln}`.trim()
  }, [prefixes])

  useEffect(() => {
    Promise.all([getGroups(), getJobTitles(1), getPrefixes()]).then(([g, j, pf]) => {
      setGroups(g.data || [])
      setJobs(j.data || [])
      setPrefixes(pf.data || [])
    })
  }, [])

  const loadMembers = useCallback(() => {
    setLoading(true)
    getGroupMembers(groupId, {
      searchText: memberSearch || undefined,
      job_title: listJobF.value || undefined,
      page,
      per_page: perPage,
      sort_by: sortBy,
      sort_dir: sortDir
    })
      .then(r => {
        const d = r?.data
        if (d && typeof d === "object" && ("rows" in d || "total" in d)) {
          setServerPaged(true)
          setRows(d.rows || [])
          setTotal(d.total || 0)
        } else {
          const arr = Array.isArray(d) ? d : (d?.data ?? [])
          setServerPaged(false)
          setRows(arr || [])
          setTotal(Array.isArray(arr) ? arr.length : 0)
        }
      })
      .catch(() => setAlert({ open: true, msg: "โหลดข้อมูลไม่สำเร็จ" }))
      .finally(() => setLoading(false))
  }, [groupId, memberSearch, listJobF.value, page, perPage, sortBy, sortDir])

  useEffect(loadMembers, [loadMembers])

  const groupOptions = useMemo(() => [{ value: "", label: "ทั้งหมด" }, ...(groups || []).map(g => ({ value: String(g.id), label: g.name }))], [groups])
  const jobOptions = useMemo(() => [{ value: "", label: "ทั้งหมด" }, ...(jobs || []).map(j => ({ value: j.job_title, label: `${j.job_title} (${j.cnt})` }))], [jobs])

  const clientFiltered = useMemo(() => {
    const q = memberSearch.trim().toLowerCase()
    const gLabel = listGroupF.label
    const jVal = listJobF.value
    const base = rows || []
    return base.filter(m => {
      const passText = !q || fullName(m).toLowerCase().includes(q) || (m.email || "").toLowerCase().includes(q) || ((m.phone_code || "") + (m.phone || "")).toLowerCase().includes(q) || (m.job_title || "").toLowerCase().includes(q)
      if (!passText) return false
      const passGroup = !listGroupF.value || (Array.isArray(m.group_names) && m.group_names.includes(gLabel)) || (typeof m.group_name === "string" && m.group_name === gLabel)
      if (!passGroup) return false
      const passJob = !jVal || (m.job_title || "") === jVal
      return passJob
    })
  }, [rows, memberSearch, listGroupF, listJobF, prefixes, fullName])

  const sortedClient = useMemo(() => {
    if (serverPaged) return clientFiltered
    const dup = [...clientFiltered]
    const getVal = (r, key) => { if (key === "full_name") return fullName(r); return r[key] }
    dup.sort((a, b) => {
      const va = getVal(a, sortBy) ?? ""
      const vb = getVal(b, sortBy) ?? ""
      if (va < vb) return sortDir === "asc" ? -1 : 1
      if (va > vb) return sortDir === "asc" ? 1 : -1
      return 0
    })
    return dup
  }, [clientFiltered, serverPaged, sortBy, sortDir, fullName])

  const displayRows = useMemo(() => {
    if (serverPaged) return rows
    const start = (page - 1) * perPage
    return sortedClient.slice(start, start + perPage)
  }, [serverPaged, rows, sortedClient, page, perPage])

  const effectiveTotal = serverPaged ? total : sortedClient.length
  const selectedSet = useMemo(() => new Set(Object.keys(selectedMap).filter(k => selectedMap[k])), [selectedMap])
  const rowKey = useCallback((u) => `${u.member_type || "learner"}_${u.user_id || u.learner_id}`, [])

  const onToggleRow = (key, row) => {
    setSelectedMap(s => {
      const n = { ...s }
      if (n[key]) {
        delete n[key]
        setSelectedRows(rm => { const t = { ...rm }; delete t[key]; return t })
      } else {
        n[key] = true
        setSelectedRows(rm => ({ ...rm, [key]: row }))
      }
      return n
    })
  }

  const onToggleAll = (keysOnPage, rowsOnPage = []) => {
    const all = keysOnPage.every(k => selectedSet.has(k))
    setSelectedMap(s => {
      const n = { ...s }
      if (all) keysOnPage.forEach(k => delete n[k])
      else keysOnPage.forEach(k => (n[k] = true))
      return n
    })
    setSelectedRows(rm => {
      const t = { ...rm }
      if (all) keysOnPage.forEach(k => delete t[k])
      else keysOnPage.forEach((k, i) => { t[k] = rowsOnPage[i] })
      return t
    })
  }

  const confirmDelete = async () => {
    if (!delItem) return
    setDeleting(true)
    try {
      if (delItem.member_type === "user") await removeGroupMember(groupId, delItem.user_id)
      else await removeGroupMember(groupId, delItem.learner_id)
      loadMembers()
    } catch { setAlert({ open: true, msg: "ไม่สามารถลบได้" }) }
    finally { setDeleting(false); setDel(null) }
  }

  const confirmBulkDelete = async () => {
    const keys = Array.from(selectedSet)
    if (keys.length === 0) { setBulkConfirm(false); return }
    setBulkDeleting(true)
    try {
      const tasks = keys.map(k => {
        const row = selectedRows[k]
        if (!row) return Promise.resolve()
        const id = row.member_type === "user" ? row.user_id : row.learner_id
        return removeGroupMember(groupId, id)
      })
      await Promise.all(tasks)
      setSelectedMap({})
      setSelectedRows({})
      loadMembers()
    } catch { setAlert({ open: true, msg: "ไม่สามารถลบได้" }) }
    finally { setBulkDeleting(false); setBulkConfirm(false) }
  }

  const columns = useMemo(() => [
    { key: "full_name", title: "ชื่อ–สกุล", sortable: true, sortKey: "full_name", render: (u) => fullName(u) },
    { key: "group_names", title: "กลุ่มผู้เรียน", headerClassName: "hidden md:table-cell", cellClassName: "hidden md:table-cell", render: (u) => Array.isArray(u.group_names) && u.group_names.length ? u.group_names.join(", ") : (typeof u.group_name === "string" && u.group_name ? u.group_name : (groupName || "Member")) },
    { key: "job_title", title: "ตำแหน่งงาน", sortable: true, render: (u) => u.job_title || "-" },
    { key: "email", title: "อีเมล", sortable: true },
    { key: "phone", title: "โทรศัพท์", render: (u) => phoneDisp(u) },
    { key: "__actions", title: "จัดการ", align: "right", width: 80, pinEnd: true, render: (u) => (<button onClick={() => setDel(u)} className="text-red-600 hover:text-red-800" title="ลบ"><Trash2 size={16} /></button>) }
  ], [fullName, groupName])

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-[1000] flex items-center justify-center p-4">
        <motion.div initial={{ y: 32, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 32, opacity: 0 }} transition={{ duration: 0.2 }} className="w-full h-full sm:h-[95vh] sm:w-[95%]  bg-white rounded-lg shadow-lg flex flex-col overflow-hidden">
          <header className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-info">
            <h2 className="flex items-center gap-2 text-sm sm:text-base font-semibold">
              <UsersIcon size={18} /> ผู้เรียนในกลุ่ม “{groupName}” ({serverPaged ? total : clientFiltered.length})
            </h2>
            <button onClick={onClose} className="text-gray-600 hover:text-primary"><X size={20} /></button>
          </header>

          <div className="px-4 py-2 border-b border-gray-200 flex items-center gap-2">
            <button onClick={() => setPickerOpen(true)} className="inline-flex items-center gap-1 bg-primary hover:bg-secondary text-white text-sm rounded-lg px-3 py-1">
              <Plus size={14} /> เพิ่มผู้เรียน
            </button>
            <div className="flex-1" />
            <button disabled={selectedSet.size === 0} onClick={() => setBulkConfirm(true)} className="inline-flex items-center gap-1 text-sm rounded-lg px-3 py-1 border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50">
              <Trash2 size={14} /> ลบที่เลือก ({selectedSet.size})
            </button>
          </div>

          <div className="flex flex-wrap gap-4 items-end px-4 py-2 bg-info border-b border-gray-200">
            <div className="flex flex-col gap-1 w-48">
              <label className="flex items-center gap-1 text-sm"><Users size={16} /> กลุ่มผู้เรียน</label>
              <Select className="rs-select" classNamePrefix="rs" options={groupOptions} value={groupOptions.find(o => o.value === listGroupF.value) || groupOptions[0]} onChange={o => setListGroupF(o || { value: "", label: "ทั้งหมด" })} isSearchable />
            </div>
            <div className="flex flex-col gap-1 w-52">
              <label className="flex items-center gap-1 text-sm"><Briefcase size={16} /> ตำแหน่งงาน</label>
              <Select className="rs-select" classNamePrefix="rs" options={jobOptions} value={jobOptions.find(o => o.value === listJobF.value) || jobOptions[0]} onChange={o => { setListJobF(o || { value: "", label: "ทั้งหมด" }); setPage(1) }} isSearchable />
            </div>
            <div className="flex flex-col gap-1 flex-1 min-w-[220px]">
              <label className="flex items-center gap-1 text-sm"><Search size={16} /> ค้นหา</label>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <div className="flex items-center gap-2">
                  <input value={memberInput} onChange={e => setMI(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { setMS(memberInput); setPage(1) } }} placeholder="ค้นหาชื่อ / อีเมล / โทรศัพท์ / ตำแหน่งงาน" className="w-full border border-gray-300 rounded-lg pl-10 px-3 py-2 text-sm focus:ring-2 focus:ring-primary" />
                  <button onClick={() => { setMS(memberInput); setPage(1) }} className="inline-flex items-center gap-1 bg-secondary hover:bg-primary text-white rounded-lg px-3 py-2 text-sm">
                    <Search size={16} /> ค้นหา
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 min-h-0 p-3">
            <DataTable
              className="h-full min-h-0"
              columns={columns}
              rows={displayRows}
              loading={loading}
              total={effectiveTotal}
              page={page}
              perPage={perPage}
              sortBy={sortBy}
              sortDir={sortDir}
              onPageChange={setPage}
              onPerPageChange={(n) => { setPerPage(n); setPage(1) }}
              onSortChange={(k, d) => { setSortBy(k); setSortDir(d); setPage(1) }}
              selectable
              rowKey={rowKey}
              selectedSet={selectedSet}
              onToggleRow={onToggleRow}
              onToggleAll={onToggleAll}
            />
          </div>

          <AnimatePresence>
            {pickerOpen && (
              <GroupMembersPickerModal
                groupId={groupId}
                groupName={groupName}
                existingKeys={new Set(rows.map(m => (m.member_type === "user" ? `user_${m.user_id}` : `learner_${m.learner_id}`)))}
                groups={groups}
                jobs={jobs}
                prefixes={prefixes}
                onSuccess={() => { setPickerOpen(false); loadMembers() }}
                onClose={() => setPickerOpen(false)}
              />
            )}
          </AnimatePresence>

          {delItem && (
            <ConfirmModal
              type="confirm"
              title="ยืนยันการลบ"
              message={`ลบ “${fullName(delItem)}” ออกจากกลุ่มผู้เรียนนี้ ?`}
              pending={deleting}
              onConfirm={confirmDelete}
              onCancel={() => setDel(null)}
            />
          )}

          {bulkConfirm && (
            <ConfirmModal
              type="confirm"
              title="ยืนยันการลบหลายรายการ"
              message={`ลบผู้เรียนที่เลือกจำนวน ${selectedSet.size} รายการ ?`}
              pending={bulkDeleting}
              onConfirm={confirmBulkDelete}
              onCancel={() => setBulkConfirm(false)}
            />
          )}

          {alert.open && (
            <ConfirmModal
              type="alert"
              title="แจ้งเตือน"
              message={alert.msg}
              onClose={() => setAlert({ open: false, msg: "" })}
            />
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

function GroupMembersPickerModal({ groupId, groupName, existingKeys, groups, jobs, prefixes, onSuccess, onClose }) {
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [groupF, setGroupF] = useState({ value: "", label: "ทุกกลุ่มผู้เรียน" })
  const [jobF, setJobF] = useState({ value: "", label: "ทั้งหมด" })
  const [searchIn, setSearchIn] = useState("")
  const [search, setSearch] = useState("")
  const [selectedMap, setSelectedMap] = useState({})
  const [saving, setSaving] = useState(false)
  const [alert, setAlert] = useState({ open: false, msg: "" })
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(() => parseInt(localStorage.getItem("dt:groupMembersPicker:perPage")) || 10)
  const [sortBy, setSortBy] = useState("first_name")
  const [sortDir, setSortDir] = useState("asc")

  useEffect(() => { localStorage.setItem("dt:groupMembersPicker:perPage", String(perPage)) }, [perPage])

  const groupOpts = useMemo(() => [{ value: "", label: "ทุกกลุ่มผู้เรียน" }, ...groups.filter(g => g.id !== "member").map(g => ({ value: String(g.id), label: g.name }))], [groups])
  const jobOpts = useMemo(() => [{ value: "", label: "ทั้งหมด" }, ...jobs.map(j => ({ value: j.job_title, label: `${j.job_title} (${j.cnt})` }))], [jobs])

  const fullName = useCallback(u => {
    const pre = prefixes.find(p => p.id === u.prefix_id)?.name_th || prefixes.find(p => p.code === u.prefix)?.name_th || u.prefix || ""
    const fn = u.first_name || u.name || ""
    const ln = u.last_name || ""
    return `${pre ? pre + " " : ""}${fn} ${ln}`.trim()
  }, [prefixes])

  const load = useCallback(() => {
    setLoading(true)
    getLearners({
      status: "1",
      searchText: search || undefined,
      group_id: groupF.value || undefined,
      job_title: jobF.value || undefined,
      page,
      per_page: perPage,
      sort_by: sortBy,
      sort_dir: sortDir,
      exclude_group_id: groupId
    })
      .then((lRes) => {
        const d = lRes?.data || {}
        const arr = Array.isArray(d?.rows) ? d.rows : Array.isArray(d) ? d : Array.isArray(d?.data) ? d.data : []
        const filtered = Array.isArray(arr) ? arr.filter(u => !existingKeys?.has(`learner_${u.id}`)) : []
        setRows(filtered)
        setTotal(Number(d?.total || (Array.isArray(filtered) ? filtered.length : 0)))
        setSelectedMap({})
      })
      .catch(() => setAlert({ open: true, msg: "โหลดข้อมูลไม่สำเร็จ" }))
      .finally(() => setLoading(false))
  }, [search, groupF, jobF, page, perPage, sortBy, sortDir, groupId, existingKeys])

  useEffect(load, [load])

  const selectedSet = useMemo(() => new Set(Object.keys(selectedMap).filter(k => selectedMap[k])), [selectedMap])
  const rowKey = (u) => `learner_${u.id}`

  const onToggleRow = (key) => {
    setSelectedMap(s => {
      const n = { ...s }
      if (n[key]) delete n[key]
      else n[key] = true
      return n
    })
  }

  const onToggleAll = (keysOnPage) => {
    const all = keysOnPage.every(k => selectedSet.has(k))
    setSelectedMap(s => {
      const n = { ...s }
      if (all) keysOnPage.forEach(k => delete n[k])
      else keysOnPage.forEach(k => (n[k] = true))
      return n
    })
  }

  const save = async () => {
    const ids = Array.from(selectedSet).map(k => k.split("_")[1])
    if (ids.length === 0) return setAlert({ open: true, msg: "ยังไม่ได้เลือกผู้เรียน" })
    setSaving(true)
    try {
      const tasks = ids.map(id => addGroupMember({ group_id: groupId, learner_id: id }))
      await Promise.all(tasks)
      onSuccess && onSuccess()
    } catch { setAlert({ open: true, msg: "บันทึกไม่สำเร็จ" }) }
    finally { setSaving(false) }
  }

  const columns = useMemo(() => [
    { key: "full_name", title: "ชื่อ–สกุล", sortable: true, sortKey: "first_name", render: (u) => fullName(u) },
    { key: "group_names", title: "กลุ่มผู้เรียน", render: (u) => u.group_names?.length ? u.group_names.join(", ") : "Member" },
    { key: "job_title", title: "ตำแหน่งงาน", sortable: true, render: (u) => u.job_title || "-" },
    { key: "email", title: "อีเมล", sortable: true },
    { key: "phone", title: "โทรศัพท์", render: (u) => ((u.phone_code || "") + (u.phone || "")) || "-" },
  ], [fullName])

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-[1100] flex items-center justify-center p-4">
      <motion.div initial={{ y: 28, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 28, opacity: 0 }} transition={{ duration: 0.2 }} className="w-full h-full sm:h-[90vh] sm:w-[90%] bg-white rounded-lg shadow-lg flex flex-col overflow-hidden">
        <header className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-info">
          <h3 className="font-semibold text-sm sm:text-base">เพิ่มผู้เรียนเข้ากลุ่ม “{groupName}”</h3>
          <button onClick={onClose} className="text-gray-600 hover:text-primary"><X size={20} /></button>
        </header>

        <div className="flex flex-wrap items-end gap-3 px-4 py-2 bg-info border-b border-gray-200">
          <div className="flex flex-col gap-1 w-48">
            <label className="flex items-center gap-1 text-sm"><Users size={16} /> กลุ่มผู้เรียน</label>
            <Select classNamePrefix="rs" options={groupOpts} value={groupF} onChange={o => { setGroupF(o); setPage(1) }} isSearchable />
          </div>
          <div className="flex flex-col gap-1 w-52">
            <label className="flex items-center gap-1 text-sm"><Briefcase size={16} /> ตำแหน่งงาน</label>
            <Select classNamePrefix="rs" options={jobOpts} value={jobF} onChange={o => { setJobF(o); setPage(1) }} isSearchable />
          </div>
          <div className="flex flex-col gap-1 flex-1 min-w-[220px]">
            <label className="flex items-center gap-1 text-sm"><Search size={16} /> ค้นหา</label>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <div className="flex items-center gap-2">
                <input value={searchIn} onChange={e => setSearchIn(e.target.value)} onKeyDown={e => e.key === "Enter" && (setSearch(searchIn), setPage(1))} placeholder="ค้นหาชื่อ / อีเมล / โทรศัพท์" className="w-full border border-gray-300 rounded-lg pl-10 px-3 py-2 text-sm focus:ring-2 focus:ring-primary" />
                <button onClick={() => { setSearch(searchIn); setPage(1) }} className="inline-flex items-center gap-1 bg-secondary hover:bg-primary text-white rounded-lg px-3 py-2 text-sm">
                  <Search size={16} /> ค้นหา
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 min-h-0 p-3">
          <DataTable
            className="h-full min-h-0"
            columns={columns}
            rows={rows}
            loading={loading}
            total={total}
            page={page}
            perPage={perPage}
            sortBy={sortBy}
            sortDir={sortDir}
            onPageChange={setPage}
            onPerPageChange={(n) => { setPerPage(n); setPage(1) }}
            onSortChange={(k, d) => { setSortBy(k); setSortDir(d); setPage(1) }}
            selectable
            rowKey={rowKey}
            selectedSet={selectedSet}
            onToggleRow={onToggleRow}
            onToggleAll={onToggleAll}
          />
        </div>

        <div className="p-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-600">เลือกแล้ว {selectedSet.size} รายการ</div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-3 py-2 rounded-lg border border-gray-300 text-sm">ยกเลิก</button>
            <button onClick={save} disabled={selectedSet.size === 0 || saving} className="inline-flex items-center gap-2 bg-primary hover:bg-secondary text-white rounded-lg px-3 py-2 text-sm disabled:opacity-50">
              {saving && <LoaderCircle size={16} className="animate-spin" />} เพิ่มผู้เรียน
            </button>
          </div>
        </div>

        {alert.open && (
          <ConfirmModal
            type="alert"
            title="แจ้งเตือน"
            message={alert.msg}
            onClose={() => setAlert({ open: false, msg: "" })}
          />
        )}
      </motion.div>
    </motion.div>
  )
}
